import { useLocation } from "react-router";

import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
} from "../lib/i18n";
import { useLocale } from "../hooks/useLocale";

// Configurable via `VITE_SITE_URL` at build time so dev / staging / prod can
// each emit correct canonical + hreflang links without a code change.
const SITE_ORIGIN = (
  import.meta.env.VITE_SITE_URL ?? "https://www.albavox.com"
).replace(/\/$/, "");

type SEOProps = {
  /** Browser tab title (`<title>`). */
  title: string;
  description: string;
  /** Path *without* the locale prefix, e.g. "/" or "/privacy". */
  path: string;
  /** Open Graph / Twitter title. Defaults to `title`. */
  socialTitle?: string;
  /** Open Graph / Twitter description. Defaults to `description`. */
  socialDescription?: string;
};

const SHARE_IMAGE = {
  path: "/metadata-image.jpg",
  type: "image/jpeg",
  width: "1200",
  height: "800",
  alt: "ALBAVOX app screens — explore Albania and the Balkans",
} as const;

/**
 * Renders document metadata via React 19's native support for <title>,
 * <meta> and <link> inside components. Outputs:
 *   - <title>
 *   - <meta name="description">
 *   - Open Graph / Twitter share tags (must match `index.html` for crawlers)
 *   - <link rel="canonical">
 *   - <link rel="alternate" hreflang="..."> for all supported locales + x-default
 *
 * No react-helmet-async needed — React 19 hoists these to <head>.
 */
export function SEO({
  title,
  description,
  path,
  socialTitle,
  socialDescription,
}: SEOProps) {
  const { code } = useLocale();
  const location = useLocation();
  // Strip the leading locale segment to derive the canonical path.
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  const search = location.search;
  const shareTitle = socialTitle ?? title;
  const shareDescription = socialDescription ?? description;
  const shareImageUrl = `${SITE_ORIGIN}${SHARE_IMAGE.path}`;

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={shareTitle} />
      <meta property="og:description" content={shareDescription} />
      <meta property="og:image" content={shareImageUrl} />
      <meta property="og:image:type" content={SHARE_IMAGE.type} />
      <meta property="og:image:width" content={SHARE_IMAGE.width} />
      <meta property="og:image:height" content={SHARE_IMAGE.height} />
      <meta property="og:image:alt" content={SHARE_IMAGE.alt} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={shareTitle} />
      <meta name="twitter:description" content={shareDescription} />
      <meta name="twitter:image" content={shareImageUrl} />
      <meta name="twitter:image:alt" content={SHARE_IMAGE.alt} />


      <link
        rel="canonical"
        href={`${SITE_ORIGIN}/${code}${cleanPath === "/" ? "" : cleanPath}${search}`}
      />

      {SUPPORTED_LANGUAGES.map((lang) => (
        <link
          key={lang.code}
          rel="alternate"
          hrefLang={lang.acceptLanguageCode}
          href={`${SITE_ORIGIN}/${lang.code}${cleanPath === "/" ? "" : cleanPath}`}
        />
      ))}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={`${SITE_ORIGIN}/${DEFAULT_LANGUAGE}${cleanPath === "/" ? "" : cleanPath}`}
      />
    </>
  );
}
